import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SD_BASE } from '@sd/lib/sdPaths';
import { Header } from '@/components/layout/Header';
import { 
  ArrowLeft,
  Mail,
  Phone,
  Building,
  MapPin,
  Clock,
  Star,
  Ticket,
  MessageSquare,
  Activity,
  MoreVertical,
  Edit,
  Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

const userProfile = {
  id: 'u27',
  name: 'Emily Johnson',
  email: 'emily.johnson@globalfinance.com',
  phone: '+1 (555) 123-4567',
  company: 'Global Finance',
  department: 'Sales',
  location: 'New York, NY',
  timezone: 'America/New_York (EST)',
  supportTier: 'Premium Support',
  joinedDate: 'March 2022',
  totalTickets: 47,
  openTickets: 3,
  avgResponseTime: '2.4h',
  satisfactionScore: 4.8,
};

const recentTickets = [
  { id: 'TKT-10026', title: 'Two-factor authentication not working', status: 'closed', priority: 'P2', date: 'Jan 19' },
  { id: 'TKT-10023', title: 'VPN connection issues from home', status: 'resolved', priority: 'P2', date: 'Jan 17' },
  { id: 'TKT-10019', title: 'Need access to SharePoint site', status: 'resolved', priority: 'P3', date: 'Jan 15' },
  { id: 'TKT-10012', title: 'Outlook calendar sync issue', status: 'closed', priority: 'P3', date: 'Jan 10' },
  { id: 'TKT-10008', title: 'New laptop setup request', status: 'closed', priority: 'P4', date: 'Jan 5' },
];

const activityLog = [
  { type: 'ticket_created', message: 'Created ticket TKT-10026', time: '2 days ago' },
  { type: 'ticket_resolved', message: 'Ticket TKT-10023 was resolved', time: '4 days ago' },
  { type: 'reply', message: 'Replied to TKT-10023', time: '4 days ago' },
  { type: 'ticket_created', message: 'Created ticket TKT-10023', time: '5 days ago' },
  { type: 'satisfaction', message: 'Rated support experience 5/5', time: '1 week ago' },
  { type: 'ticket_resolved', message: 'Ticket TKT-10019 was resolved', time: '1 week ago' },
];

const assets = [
  { name: 'MacBook Pro 14"', type: 'Laptop', status: 'Active', assigned: 'Mar 2022' },
  { name: 'iPhone 14 Pro', type: 'Mobile', status: 'Active', assigned: 'Sep 2023' },
  { name: 'Dell Monitor 27"', type: 'Monitor', status: 'Active', assigned: 'Mar 2022' },
];

const statusColors = {
  open: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  pending: 'bg-warning/20 text-warning border-warning/30',
  resolved: 'bg-success/20 text-success border-success/30',
  closed: 'bg-muted text-muted-foreground border-border',
};

const UserProfile = () => {
  const navigate = useNavigate();

  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      <Header />
      
      <ScrollArea className="flex-1">
        <div className="max-w-5xl mx-auto p-8">
          {/* Back Button */}
          <button
            onClick={() => navigate(`${SD_BASE}/`)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to tickets
          </button>

          {/* Profile Header */}
          <div className="flex items-start gap-6 mb-8">
            <div className="h-24 w-24 rounded-2xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
              <span className="text-3xl font-bold text-primary-foreground">
                {userProfile.name.split(' ').map(n => n[0]).join('')}
              </span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-foreground">{userProfile.name}</h1>
                <Badge className="bg-warning/20 text-warning border-warning/30">
                  <Star className="h-3 w-3 mr-1" />
                  {userProfile.supportTier}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Mail className="h-4 w-4" />
                  {userProfile.email}
                </span>
                <span className="flex items-center gap-1.5">
                  <Phone className="h-4 w-4" />
                  {userProfile.phone}
                </span>
                <span className="flex items-center gap-1.5">
                  <Building className="h-4 w-4" />
                  {userProfile.company}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-2">
                <span className="flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" />
                  {userProfile.location}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />
                  {userProfile.timezone}
                </span>
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Profile
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Shield className="h-4 w-4 mr-2" />
                  Change Support Tier
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Send Message
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="p-4 rounded-xl border border-border bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Ticket className="h-5 w-5 text-primary" />
                </div>
              </div>
              <p className="text-2xl font-bold text-foreground">{userProfile.totalTickets}</p>
              <p className="text-sm text-muted-foreground">Total Tickets</p>
            </div>
            <div className="p-4 rounded-xl border border-border bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Activity className="h-5 w-5 text-blue-500" />
                </div>
              </div>
              <p className="text-2xl font-bold text-foreground">{userProfile.openTickets}</p>
              <p className="text-sm text-muted-foreground">Open Tickets</p>
            </div>
            <div className="p-4 rounded-xl border border-border bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-lg bg-success/10 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-success" />
                </div>
              </div>
              <p className="text-2xl font-bold text-foreground">{userProfile.avgResponseTime}</p>
              <p className="text-sm text-muted-foreground">Avg Response</p>
            </div>
            <div className="p-4 rounded-xl border border-border bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-lg bg-warning/10 flex items-center justify-center">
                  <Star className="h-5 w-5 text-warning" />
                </div>
              </div>
              <p className="text-2xl font-bold text-foreground">{userProfile.satisfactionScore}/5</p>
              <p className="text-sm text-muted-foreground">Satisfaction</p>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="tickets" className="space-y-6">
            <TabsList className="bg-muted/50">
              <TabsTrigger value="tickets">Recent Tickets</TabsTrigger>
              <TabsTrigger value="activity">Activity Log</TabsTrigger>
              <TabsTrigger value="assets">Assets</TabsTrigger>
            </TabsList>

            <TabsContent value="tickets" className="space-y-3">
              {recentTickets.map((ticket) => (
                <button
                  key={ticket.id}
                  onClick={() => navigate(`${SD_BASE}/`)}
                  className="w-full p-4 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors text-left flex items-center gap-4"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-xs font-mono text-muted-foreground">{ticket.id}</span>
                      <Badge className={cn("text-xs", statusColors[ticket.status as keyof typeof statusColors])}>
                        {ticket.status}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {ticket.priority}
                      </Badge>
                    </div>
                    <p className="text-sm font-medium text-foreground">{ticket.title}</p>
                  </div>
                  <span className="text-xs text-muted-foreground">{ticket.date}</span>
                </button>
              ))}
            </TabsContent>

            <TabsContent value="activity" className="space-y-3">
              {activityLog.map((activity, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-4 rounded-lg border border-border bg-card"
                >
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                    <Activity className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-foreground">{activity.message}</p>
                  </div>
                  <span className="text-xs text-muted-foreground">{activity.time}</span>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="assets" className="space-y-3">
              {assets.map((asset, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 p-4 rounded-lg border border-border bg-card"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{asset.name}</p>
                    <p className="text-xs text-muted-foreground">{asset.type}</p>
                  </div>
                  <Badge variant="outline" className="bg-success/10 text-success border-success/30">
                    {asset.status}
                  </Badge>
                  <span className="text-xs text-muted-foreground">Since {asset.assigned}</span>
                </div>
              ))}
            </TabsContent>
          </Tabs>
        </div>
      </ScrollArea>
    </div>
  );
};

export default UserProfile;
